import React, { FunctionComponent, useContext, useState } from "react";
import { RouteComponentProps, withRouter } from "react-router";
import { Button } from "@cmsgov/ds-medicare-gov";
import { useTranslate } from "../../helpers/intlHooks";
import { ActionType, County } from "../../@types";
import { CountyPicker } from "../../components";
import routes from "../../app/routes";
import { CoverageType } from "./types";
import URI from "urijs";
import { parseSearchParams } from "../../helpers";
import { getSearchPlanTypeForCoverageType } from "../../helpers/questionsRoutingHelpers";
import { AppContext } from "../../app/store";
import {
  AnalyticsActionType,
  AnalyticsButtonStyle,
  AnalyticsButtonType,
} from "../../app/contexts/Analytics/types";

interface CountySelectionProps extends RouteComponentProps {
  coverageType: CoverageType;
  onNavigation?: () => void;
}

export const countyActions = (newCounty: County) => [
  { type: ActionType.UPDATE_COUNTY, payload: newCounty },
  { type: ActionType.UPDATE_FIPS, payload: newCounty.fips },
];

export const zipCodeActions = (zipcode: string) => [
  { type: ActionType.UPDATE_ZIPCODE, payload: zipcode },
  { type: ActionType.UPDATE_PHARMACY_ADDRESS, payload: "" },
];

export const nextRoutePath = routes.lisQuestions;

const CountySelection: FunctionComponent<CountySelectionProps> = ({
  coverageType,
  onNavigation = () => {},
  location,
  history,
}) => {
  const t = useTranslate();
  const { dispatch } = useContext(AppContext);
  const [county, setCounty] = useState<County | undefined>(undefined);
  const uri = new URI(location.search);
  const { fips: urlFips, zip: urlZip } = parseSearchParams(uri.search(true));
  const showContinue = !!county;

  const fips = county ? county.fips : "0";
  const planType = getSearchPlanTypeForCoverageType(coverageType);

  const handleCountySelected = (newCounty?: County, zipcode?: string): void => {
    setCounty(newCounty);
    if (newCounty) {
      countyActions(newCounty).forEach(action => dispatch(action));
    }
    if (zipcode) {
      dispatch({
        type: AnalyticsActionType.SEND_TEALIUM_EVENT,
        settings: {
          event_action: "Find Plans - Choose Plans",
          event_label: "Enter Zip Code",
        },
      });
      zipCodeActions(zipcode).forEach(action => dispatch(action));
    }
  };

  const onContinue = (): void => {
    const nextRoute = `${routes.lisQuestions}?fips=${fips}&plan_type=${planType}`;
    dispatch({
      type: AnalyticsActionType.SEND_TEALIUM_EVENT,
      settings: {
        event_action: "Find Plans",
        event_label: "Continue",
      },
    });
    dispatch({
      type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
      settings: {
        button: {
          buttonStyle: AnalyticsButtonStyle.PRIMARY,
          buttonType: AnalyticsButtonType.BUTTON,
          text: t("landing_page.continue"),
        },
        linkUrl: nextRoute,
      },
    });
    onNavigation();

    history.push(nextRoute);
  };

  return (
    <div className="ds-u-margin-top--5">
      <CountyPicker
        label={t("plan_search.enter_your_zip_code")}
        onCountySelect={handleCountySelected}
        initialFips={urlFips}
        initialZip={urlZip}
      />
      {showContinue && (
        <section data-testid="continue" className="ContinueSection">
          <Button
            variation="solid"
            className="mct-u-width--full mct-u-md-width--auto"
            onClick={onContinue}
          >
            {t("landing_page.continue")}
          </Button>
        </section>
      )}
    </div>
  );
};

export default withRouter(CountySelection);
